import { isEmpty, MedplumClient } from "@medplum/core";
import { Questionnaire } from "@medplum/fhirtypes";

import { fetchClientIntakeQuestionnaire } from "@/clients/services/client-profile";
import { fetchIntakeQuestionnaire } from "@/consents/services/consents";
import { isDefined } from "@/utils/array";

import { IntakeDependentFeature } from "../types/client-intake";
import { getRemovedIntakeItemLabels } from "../utils/intake-dependencies";

/**
 * Lists the intake content a feature lost for one client.
 *
 * An item counts when the client's own intake version lacks it and the practice's active intake
 * still lacks it. Content the practice has turned back on is left out.
 *
 * @param medplum - the Medplum client.
 * @param params - `feature` reading the data, `practiceId` of the active intake, and `clientId`.
 *   Pass `clientIntake` when the screen already loaded the version the client's intake response was
 *   answered on, to skip reading it again.
 * @returns labels of the removed content, or an empty list when nothing is missing or the client
 *   has no intake response.
 */
export const fetchRemovedIntakeItemLabels = async (
  medplum: MedplumClient,
  { feature, practiceId, clientId, clientIntake }: RemovedIntakeItemLabelsParams,
): Promise<string[]> => {
  const clientQuestionnaire =
    clientIntake ?? (await fetchClientIntakeQuestionnaire(medplum, clientId));

  if (!clientQuestionnaire || isEmpty(getRemovedIntakeItemLabels([clientQuestionnaire], feature))) {
    return [];
  }

  const activeIntake = await fetchIntakeQuestionnaire(medplum, practiceId);
  return getRemovedIntakeItemLabels([clientQuestionnaire, activeIntake].filter(isDefined), feature);
};

interface RemovedIntakeItemLabelsParams {
  feature: IntakeDependentFeature;
  practiceId: string;
  clientId: string;
  clientIntake?: Questionnaire;
}
